import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import DateRange2 from "../../Component/DateRange/DateRange2";
import { post } from "../../Services/ApiHandler";
import { URLS } from "../../Services/Constant";
import { Toast } from "../../Component/Toast";
import SubmitButton from "../../Component/SubmitButton";
import CommonPopup from "../../Component/CommonPopup";
import { PRIMARY_COLOR } from "../../Services/Colors";

// Convert "DD-MM-YYYY" → "YYYY-MM-DD"
const convertDateToISO = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length !== 3) return "";
    const [dd, mm, yyyy] = parts;
    return `${yyyy}-${mm}-${dd}`;
};

const AnnounceResultEdit = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { editData } = location.state || {};

    const [loading, setLoading] = useState(false);
    const [popup, setPopup] = useState(false);
    const [drawDate, setDrawDate] = useState("");
    const [lotDraw, setLotDraw] = useState("");
    const [timeDraw, setTimeDraw] = useState(null);
    const [selectedTimeId, setSelectedTimeId] = useState(null);
    const [resultNumber, setResultNumber] = useState(["", "", "", ""]);

    const [errors, setErrors] = useState({});
    const [showResult, setShowResult] = useState(false);

    const [backupData, setBackupData] = useState(null);

    const [lotOptions, setLotOptions] = useState([]);
    const [timeOptions, setTimeOptions] = useState([]);

    // ----------------------------------------------------------------
    // INITIAL LOAD FOR EDIT MODE
    // ----------------------------------------------------------------
    useEffect(() => {
        if (!editData) return;

        // DATE
        const isoDate = convertDateToISO(editData.date);
        setDrawDate(isoDate);

        // LOT NAME
        const lotObj = { value: editData.lot_name, label: editData.lot_name };
        setLotOptions([lotObj]);
        setLotDraw(lotObj);

        // TIME
        const timeObj = {
            value: editData.time,
            label: editData.time,
            id: editData.id
        };
        setTimeOptions([timeObj]);
        setTimeDraw(timeObj);
        setSelectedTimeId(editData.id);

        // RESULT NUMBER
        if (editData.winning_number) {
            const num = editData.winning_number.toString().split("");
            while (num.length < 4) num.push("");
            setResultNumber(num);
        }
    }, [editData]);

    // ----------------------------------------------------------------
    // INPUT HANDLER
    // ----------------------------------------------------------------
    const handleChange = (e) => {
        const value = e.target.value.replace(/\D/g, "").slice(0, 4);
        const arr = value.split("");
        while (arr.length < 4) arr.push("");
        setResultNumber(arr);
    };

    // ----------------------------------------------------------------
    // CHECK RESULT
    // ----------------------------------------------------------------
    const handleCheckResult = () => {
        let newErrors = {};

        if (!lotDraw) newErrors.lot = "Please select Lot Name";
        if (!timeDraw) newErrors.time = "Please select Time";
        if (resultNumber.join("").length !== 4)
            newErrors.resultNumber = "Please enter 4 digits";

        setErrors(newErrors);

        if (Object.keys(newErrors).length !== 0) {
            setShowResult(false);
            return;
        }

        // Save values before clearing
        setBackupData({
            drawDate,
            lotDraw,
            timeDraw,
            resultNumber,
        });

        // Show preview
        setShowResult(true);

        // Clear all inputs exactly as you want
        setDrawDate("");
        setLotDraw("");
        setTimeDraw(null);
        setResultNumber(["", "", "", ""]);
    };

    // ----------------------------------------------------------------
    // EDIT BUTTON → RESTORE DATA
    // ----------------------------------------------------------------
    const handleEdit = () => {
        if (!backupData) return;

        setDrawDate(backupData.drawDate);
        setLotDraw(backupData.lotDraw);
        setTimeDraw(backupData.timeDraw);
        setResultNumber(backupData.resultNumber);

        setShowResult(false);
        setErrors({});
    };

    // ----------------------------------------------------------------
    // ANNOUNCE RESULT API
    // ----------------------------------------------------------------
    const handleAnnounceResult = async () => {
        setLoading(true);

        const postData = {
            id: selectedTimeId,
            winning_number: backupData?.resultNumber.join("")
        };

        const res = await post(URLS.RESULTANNOUNCE, postData);

        if (res.status === "success") {
            Toast(res.message || "Result Announced Successfully");
            setPopup(false);
            navigate(-1);
        }
        setLoading(false);
    };

    // ----------------------------------------------------------------
    // SELECT STYLES
    // ----------------------------------------------------------------
    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            borderRadius: 8,
            border: `1px solid ${PRIMARY_COLOR}`,
            border: `1px solid ${state.isFocused || state.hasValue ? PRIMARY_COLOR : `${PRIMARY_COLOR}`}`,
            boxShadow: state.isFocused
                ? "transparent"
                : "0 0 0 1px #dffffd66",
            "&:hover": {
                border: `1px solid ${PRIMARY_COLOR}`,
            },
            backgroundColor: "#F0EDE6",
            minHeight: 51,
            padding: "3px 10px",
            fontFamily: state.isSelected ? "f3" : "f2",
            color: PRIMARY_COLOR,
        }),

        menu: (provided) => ({
            ...provided,
            zIndex: 9999,
            backgroundColor: "#F0EDE6",
            borderRadius: 8,
        }),

        menuList: (provided) => ({
            ...provided,
            backgroundColor: "#F0EDE6",
            borderRadius: 8,
        }),

        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? "transparent"
                : state.isFocused
                    ? "transparent"
                    : "transparent",
            fontFamily: "f2",
            color: state.isSelected
                ? PRIMARY_COLOR
                : PRIMARY_COLOR,
            cursor: "pointer",
            ":active": {
                backgroundColor: `${PRIMARY_COLOR}33`,
            },
        }),

        singleValue: (provided) => ({
            ...provided,
            color: PRIMARY_COLOR,
        }),

        indicatorSeparator: () => ({
            display: "none",
        }),

        dropdownIndicator: (provided) => ({
            ...provided,
            color: PRIMARY_COLOR,
            "&:hover": {
                color: PRIMARY_COLOR,
            },
        }),
    };

    return (
        <div className="dashRightView ">
            <div style={{ paddingTop: 0 }} className="container_overall">
                {/* DATE */}
                <div className="announcement_date mb-3 col-md-5">
                    <DateRange2 selectedDate={drawDate} setSelectedDate={setDrawDate} />
                </div>

                {/* LOT NAME */}
                <div className="my-2 pb-2 col-md-5">
                    <label className="form-label f2 primary1">Lot Name</label>
                    <Select
                        className="location-search"
                        value={lotDraw}
                        onChange={setLotDraw}
                        options={lotOptions}
                        placeholder="Select Lot"
                        styles={customStyles}
                        isSearchable={false}
                        noOptionsMessage={() => ""}
                    />
                    {errors.lot && <p className="err">{errors.lot}</p>}
                </div>

                {/* TIME */}
                <div className="my-2 pb-2 col-md-5">
                    <label className="form-label f2 primary1">Time</label>
                    <Select
                        className="location-search"
                        value={timeDraw}
                        onChange={(selected) => {
                            setTimeDraw(selected);
                            setSelectedTimeId(selected?.id);
                        }}
                        options={timeOptions}
                        placeholder="Select Time"
                        styles={customStyles}
                        isSearchable={false}
                        noOptionsMessage={() => ""}
                    />
                    {errors.time && <p className="err">{errors.time}</p>}
                </div>

                {/* RESULT NUMBER */}
                <div className="my-2 pb-2 col-md-5">
                    <label className="form-label f2 primary1">Result Number</label>
                    <input
                        className="input-box placholdercolorchanges2"
                        type='number'
                        value={resultNumber.join("")}
                        onChange={handleChange}
                        maxLength={4}
                        placeholder="Enter Result Number"
                    />
                    {errors.resultNumber && <p className="err">{errors.resultNumber}</p>}
                </div>

                <div className="my-3 col-md-5">
                    {/* <button className="add-button f3 w-100 fs-xs-15 fs-sm-11 fs-md-13 fs-lg-15 fs-xl-17 fs-xxl-19 sec" onClick={handleCheckResult}>
                        Check Result
                    </button> */}
                    <SubmitButton
                        title="Check Result"
                        onClick={handleCheckResult}
                        isFormValid={false}
                    />
                </div>

                {/* PREVIEW SECTION */}
                {showResult && (
                    <div className=" col-md-5">
                        <div className="d-flex ac-jb">
                            <label className="form-label f3 fs-md-14 fs-lg-16 m-0 primary">Check Result</label>
                            <button className="f3 fs-sm-8 fs-md-9 fs-lg-11 fs-xl-13 fs-xxl-15 edit1-btn2" onClick={handleEdit}>Edit</button>
                        </div>

                        <div
                            style={{ backgroundColor: "#F0EDE6", borderRadius: 20 }}
                            className="check_result border shadow-sm">
                            <div className="d-flex ac-jb my-2">
                                <h4 className="f3 fs-xs-16 fs-sm-16 fs-md-18 fs-lg-16">Date</h4>
                                <p className="f2 fs-xs-16 fs-sm-16 fs-md-18 fs-lg-16 gray">
                                    {new Date(backupData?.drawDate)
                                        .toLocaleDateString("en-GB")
                                        .replace(/\//g, "-")}
                                </p>
                            </div>

                            <div className="d-flex ac-jb my-2">
                                <p className="f3 fs-xs-16 fs-sm-16 fs-md-18 fs-lg-16">Lot Name</p>
                                <p className="f2 fs-xs-16 fs-sm-16 fs-md-18 fs-lg-16 gray">{backupData?.lotDraw?.label}</p>
                            </div>

                            <div className="d-flex ac-jb my-2">
                                <p className="f3 fs-xs-16 fs-sm-16 fs-md-18 fs-lg-16">Time</p>
                                <p className="f2 fs-xs-16 fs-sm-16 fs-md-18 fs-lg-16 gray">{backupData?.timeDraw?.label}</p>
                            </div>

                            <div className="d-flex ac-jb">
                                <p className="f3 fs-xs-16 fs-sm-16 fs-md-18 fs-lg-16">Result Number</p>
                                <div className="d-flex gap-2">
                                    {backupData?.resultNumber?.map((n, i) => (
                                        <input
                                            key={i}
                                            value={n}
                                            readOnly
                                            className="result-input f3 text-center fs-xs-16 fs-sm-12 fs-md-14 fs-lg-14 fs-xl-15 fs-xxl-20 sec placholdercolorchanges2"
                                            style={{
                                                width: 28,
                                                height: 28,
                                                borderRadius: 30,
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* <button
             
              onClick={() => setPopup(true)}
              className="add-button f3 w-100 fs-xs-15 fs-sm-11 fs-md-13 fs-lg-15 fs-xl-17 fs-xxl-19 white"
            >
              Announce Result
            </button> */}

                        <div style={{ marginBottom: 80, marginTop: 20 }}>
                            <SubmitButton
                                title="Announce Result"
                                onClick={() => setPopup(true)}
                                isFormValid={false}
                            />
                        </div>
                    </div>
                )}

                {/* {popup && (
                    <div className="img-popup-design">
                        <div className="img-popup">
                            <p className="f3 fs-xs-14 fs-sm-15 fs-md-15 fs-lg-15 fs-xl-17 fs-xxl-19 primary">
                                Are you sure you want to Announce Result?
                            </p>
                            <div style={{ paddingTop: 20 }} className="d-flex ac-jb px-2 f3 fs-xs-14 fs-sm-15 fs-sm-11 fs-md-13 fs-lg-15 fs-xl-17 fs-xxl-19">
                                <button onClick={() => { setPopup(false) }} className="no-btn w-40">
                                    No
                                </button>
                                <button
                                    onClick={handleAnnounceResult}
                                    className="yes-btn f2 w-40 f3 fs-xs-14 fs-sm-15 fs-sm-11 fs-md-13 fs-lg-15 fs-xl-17 fs-xxl-19"
                                >
                                    {loading ? <>
                                        <span
                                            className="spinner-border spinner-border-sm"
                                            role="status"
                                            aria-hidden="true"
                                            style={{ color: "black" }}
                                        ></span>
                                    </> : 'Yes'}

                                </button>
                            </div>
                        </div>
                    </div>
                )} */}

                <CommonPopup
                    isOpen={popup}
                    message="Are you sure you want to Announce Result?"
                    onClose={() => { setPopup(false) }}
                    onConfirm={handleAnnounceResult}
                    loading={loading}
                />
            </div>
        </div>
    );
};

export default AnnounceResultEdit;
